import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import Seo from '../components/Seo';
import ArcadeGameCard from '../components/ArcadeGameCard';
import apiClient from '../services/api';
import { Loader2, Flame, Clock } from 'lucide-react';

export default function Arcade() {
  const [games, setGames] = useState([]);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState('popular');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    apiClient
      .get(`/arcade/games?sort=${sort}&limit=60`)
      .then((res) => {
        if (!active) return;
        setGames(res.data.games || []);
        setTotal(res.data.total || 0);
      })
      .catch((err) => console.error('Failed to load arcade', err))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [sort]);

  return (
    <div className="min-h-screen bg-black">
      <Seo
        title="Game Arcade – Play Free AI-Built Games | GameOS"
        description="Browse and play free HTML5 games built with AI on GameOS. Discover 2D platformers, 3D worlds and arcade classics made by creators — then remix them into your own."
        path="/games"
      />
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20">
        <div className="mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3">
            The
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"> Arcade</span>
          </h1>
          <p className="text-gray-400 max-w-2xl">
            Every game here was built with AI by a GameOS creator. Play free in your browser, then hit
            "Remix with AI" on any game to fork it into your own.
          </p>
        </div>

        <div className="flex items-center gap-2 mb-8">
          <button
            data-testid="arcade-sort-popular"
            onClick={() => setSort('popular')}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
              sort === 'popular' ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <Flame size={15} /> Popular
          </button>
          <button
            data-testid="arcade-sort-newest"
            onClick={() => setSort('newest')}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
              sort === 'newest' ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <Clock size={15} /> Newest
          </button>
          <span className="text-gray-500 text-sm ml-auto" data-testid="arcade-total-count">
            {total} games
          </span>
        </div>

        {loading ? (
          <div className="flex justify-center py-24 text-gray-400">
            <Loader2 className="animate-spin mr-2" /> Loading games...
          </div>
        ) : games.length === 0 ? (
          <div className="text-center py-24 text-gray-400">No games yet — be the first to build one!</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="arcade-grid">
            {games.map((g) => (
              <ArcadeGameCard key={g.game_id} game={g} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
